import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/database/userService';
import { 
  Camera, 
  Save, 
  ArrowLeft, 
  User, 
  Bell, 
  Shield, 
  Palette,
  Upload,
  X,
  CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const SettingsPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    displayName: '',
    phone: '',
    address: '',
    bio: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Theme settings
  const [theme, setTheme] = useState<'light' | 'dark' | 'auto'>('light');
  
  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [newLessonNotifications, setNewLessonNotifications] = useState(true);
  
  // Privacy settings
  const [publicProfile, setPublicProfile] = useState(false);
  const [showProgress, setShowProgress] = useState(true);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (user) {
        try {
          // Load existing profile data from database
          const existingProfile = await userService.getUserProfile(user.id);
          if (existingProfile) {
            setProfileData({
              displayName: existingProfile.fullName || user.name || '',
              phone: existingProfile.phone || '',
              address: existingProfile.address || '',
              bio: existingProfile.bio || ''
            });
          } else {
            // Fallback to user data if no profile exists
            setProfileData(prev => ({
              ...prev,
              displayName: user.name || ''
            }));
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
          // Fallback to user data
          setProfileData(prev => ({
            ...prev,
            displayName: user.name || ''
          }));
        }
      }
    };

    loadUserProfile();
  }, [user]);

  // Load settings from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'auto' | null;
    const savedEmailNotifications = localStorage.getItem('emailNotifications');
    const savedNewLessonNotifications = localStorage.getItem('newLessonNotifications');
    const savedPublicProfile = localStorage.getItem('publicProfile');
    const savedShowProgress = localStorage.getItem('showProgress');
    
    if (savedTheme) setTheme(savedTheme);
    if (savedEmailNotifications !== null) setEmailNotifications(savedEmailNotifications === 'true');
    if (savedNewLessonNotifications !== null) setNewLessonNotifications(savedNewLessonNotifications === 'true');
    if (savedPublicProfile !== null) setPublicProfile(savedPublicProfile === 'true');
    if (savedShowProgress !== null) setShowProgress(savedShowProgress === 'true');
  }, []);

  // Apply theme to document
  useEffect(() => {
    const applyTheme = (selectedTheme: 'light' | 'dark' | 'auto') => {
      let effectiveTheme = selectedTheme;
      
      if (selectedTheme === 'auto') {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        effectiveTheme = prefersDark ? 'dark' : 'light';
      }
      
      if (effectiveTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };
    
    applyTheme(theme);
    
    // Listen for system theme changes when in auto mode
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme('auto');
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        alert('File quá lớn. Vui lòng chọn file nhỏ hơn 2MB.');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('Chỉ hỗ trợ file JPG, PNG, GIF.');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'auto') => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const handleEmailNotificationsChange = (checked: boolean) => {
    setEmailNotifications(checked);
    localStorage.setItem('emailNotifications', String(checked));
  };

  const handleNewLessonNotificationsChange = (checked: boolean) => {
    setNewLessonNotifications(checked);
    localStorage.setItem('newLessonNotifications', String(checked));
  };

  const handlePublicProfileChange = (checked: boolean) => {
    setPublicProfile(checked);
    localStorage.setItem('publicProfile', String(checked));
  };

  const handleShowProgressChange = (checked: boolean) => {
    setShowProgress(checked);
    localStorage.setItem('showProgress', String(checked));
  };

  const handleSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      // Update user profile in database
      const updatedProfile = await userService.updateUserProfile(user.id, {
        fullName: profileData.displayName,
        phone: profileData.phone,
        address: profileData.address,
        bio: profileData.bio,
      });
      
      console.log('Profile updated successfully:', updatedProfile);
      alert('Đã lưu cài đặt thành công!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Có lỗi xảy ra khi lưu cài đặt. Vui lòng thử lại.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Vui lòng đăng nhập</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Quay về trang chủ
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', name: 'Hồ sơ', icon: User },
    { id: 'notifications', name: 'Thông báo', icon: Bell },
    { id: 'privacy', name: 'Bảo mật', icon: Shield },
    { id: 'appearance', name: 'Giao diện', icon: Palette }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center space-x-4">
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">Quay lại</span>
            </Link>
            <div className="h-4 sm:h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Cài đặt</h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 sm:py-3 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="font-medium text-sm sm:text-base">{tab.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* Avatar Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-4 sm:p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ảnh đại diện</h2>
                  <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="relative">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                        {uploadedImage ? (
                          <img 
                            src={uploadedImage} 
                            alt="Avatar" 
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-white font-bold text-xl sm:text-2xl drop-shadow-sm">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        )}
                      </div>
                      {uploadedImage && (
                        <button
                          onClick={() => setUploadedImage(null)}
                          className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="text-center sm:text-left">
                      <button
                        onClick={handleFileUpload}
                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors w-full sm:w-auto"
                      >
                        <Camera className="w-4 h-4" />
                        <span>Thay đổi ảnh</span>
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">JPG, PNG, GIF (tối đa 2MB)</p>
                    </div>
                  </div>
                </div>

                {/* Profile Information */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-4 sm:p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Thông tin cá nhân</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tên hiển thị</label>
                      <input
                        type="text"
                        value={profileData.displayName}
                        onChange={(e) => handleInputChange('displayName', e.target.value)}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Nhập tên hiển thị"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Số điện thoại</label>
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Nhập số điện thoại"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Địa chỉ</label>
                      <input
                        type="text"
                        value={profileData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Nhập địa chỉ"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Giới thiệu bản thân</label>
                      <textarea
                        rows={4}
                        value={profileData.bio}
                        onChange={(e) => handleInputChange('bio', e.target.value)}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base resize-none placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Viết vài dòng giới thiệu về bản thân..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cài đặt thông báo</h2>
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">Email thông báo</h3>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Nhận thông báo qua email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={emailNotifications}
                        onChange={(e) => handleEmailNotificationsChange(e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">Thông báo bài học mới</h3>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Thông báo khi có bài học mới</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={newLessonNotifications}
                        onChange={(e) => handleNewLessonNotificationsChange(e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bảo mật & Quyền riêng tư</h2>
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">Hiển thị hồ sơ công khai</h3>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Cho phép người khác xem thông tin cơ bản</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={publicProfile}
                        onChange={(e) => handlePublicProfileChange(e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">Hiển thị tiến độ học tập</h3>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Chia sẻ tiến độ học tập với bạn bè</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={showProgress}
                        onChange={(e) => handleShowProgressChange(e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Giao diện</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Chủ đề</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <button 
                        onClick={() => handleThemeChange('light')}
                        className={`p-3 border-2 rounded-lg transition-all ${
                          theme === 'light' 
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 shadow-md' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="w-full h-6 sm:h-8 bg-white rounded mb-2 shadow-sm"></div>
                        <span className="text-xs sm:text-sm font-medium dark:text-gray-300">Sáng</span>
                        {theme === 'light' && (
                          <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mx-auto mt-2" />
                        )}
                      </button>
                      <button 
                        onClick={() => handleThemeChange('dark')}
                        className={`p-3 border-2 rounded-lg transition-all ${
                          theme === 'dark' 
                            ? 'border-blue-500 bg-blue-50 dark:bg-gray-900 shadow-md' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="w-full h-6 sm:h-8 bg-gray-800 rounded mb-2 shadow-sm"></div>
                        <span className="text-xs sm:text-sm font-medium dark:text-gray-300">Tối</span>
                        {theme === 'dark' && (
                          <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mx-auto mt-2" />
                        )}
                      </button>
                      <button 
                        onClick={() => handleThemeChange('auto')}
                        className={`p-3 border-2 rounded-lg transition-all ${
                          theme === 'auto' 
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 shadow-md' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="w-full h-6 sm:h-8 bg-gradient-to-r from-white to-gray-800 rounded mb-2 shadow-sm"></div>
                        <span className="text-xs sm:text-sm font-medium dark:text-gray-300">Tự động</span>
                        {theme === 'auto' && (
                          <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mx-auto mt-2" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {theme === 'light' && '💡 Giao diện sáng luôn được áp dụng'}
                      {theme === 'dark' && '🌙 Giao diện tối luôn được áp dụng'}
                      {theme === 'auto' && '🔄 Tự động theo cài đặt hệ thống'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="mt-6 flex justify-center sm:justify-end">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center justify-center space-x-2 px-6 py-2 sm:py-3 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 dark:disabled:opacity-40 w-full sm:w-auto text-sm sm:text-base shadow-md hover:shadow-lg"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Đang lưu...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Lưu thay đổi</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
